/**
 * Paint calculator: add/remove wall and opening rows; run calculation on Calculate click.
 * Uses <paint-calculator> custom element; all selectors use data attributes.
 */
(function () {
  const DATA = {
    wallsList: 'data-paint-walls-list',
    wallItem: 'data-paint-wall-item',
    addWall: 'data-paint-add-wall',
    openingsList: 'data-paint-openings-list',
    openingItem: 'data-paint-opening-item',
    addOpening: 'data-paint-add-opening',
    rowLabel: 'data-paint-row-label',
    rowHeader: 'data-paint-row-header',
    remove: 'data-paint-remove',
    results: 'data-paint-calc-results',
    trigger: 'data-paint-calc-trigger',
    totalWallValue: 'data-paint-total-wall-value',
    subtractedValue: 'data-paint-subtracted-value',
    netValue: 'data-paint-net-value',
    litersValue: 'data-paint-liters-value',
    totalArea: 'data-paint-total-area',
    basedOn: 'data-paint-based-on',
    coatsLabel: 'data-paint-coats-label',
  };

  class PaintCalculator extends HTMLElement {
    connectedCallback() {
      const root = this;
      const wallsList = root.querySelector(`[${DATA.wallsList}]`);
      const wallItem = root.querySelector(`[${DATA.wallItem}]`);
      const addWallBtn = root.querySelector(`[${DATA.addWall}]`);
      const openingsList = root.querySelector(`[${DATA.openingsList}]`);
      const openingItem = root.querySelector(`[${DATA.openingItem}]`);
      const addOpeningBtn = root.querySelector(`[${DATA.addOpening}]`);
      const calcTrigger = root.querySelector(`[${DATA.trigger}]`);

      // Prevent invalid numeric input (e.g., negative values) across all number fields.
      root.addEventListener(
        'input',
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement)) return;
          if (target.type !== 'number') return;
          enforceNumberConstraints(target);
        },
        true
      );

      if (addWallBtn && wallItem && wallsList) {
        addWallBtn.addEventListener('click', () => appendRow(root, 'wall'));
      }
      if (addOpeningBtn && openingItem && openingsList) {
        addOpeningBtn.addEventListener('click', () => appendRow(root, 'opening'));
      }
      if (calcTrigger) {
        calcTrigger.addEventListener('click', () => {
          const data = runCalculation(root);
          showResults(root, data);
        });
      }
      root.addEventListener('click', onRemoveClick);

      // Hide results when popup (dialog) opens or closes; only show after Calculate is clicked.
      const dialogComponent = root.closest('dialog-component');
      if (dialogComponent) {
        dialogComponent.addEventListener('dialog:open', () => hideResults(root));
        dialogComponent.addEventListener('dialog:close', () => hideResults(root));
      }
    }
  }

  /**
   * Reads all wall/opening inputs and paint details; returns calculated values.
   * Formulas: Total Wall Area = sum(Length*Height*Multiply) per wall; Subtracted = sum per opening;
   * Net = Total - Subtracted; Liters = (Net * Coats) / Spreading rate.
   */
  function runCalculation(/** @type {HTMLElement} */ root) {
    const totalWallArea = sumRowAreas(root.querySelectorAll(`[${DATA.wallItem}]`));
    const subtractedArea = sumRowAreas(root.querySelectorAll(`[${DATA.openingItem}]`));
    const netArea = totalWallArea - subtractedArea;
    const coatsInput = /** @type {HTMLInputElement|null} */ (root.querySelector('input[name="number_of_coats"]'));
    const rateInput = /** @type {HTMLInputElement|null} */ (root.querySelector('input[name="spreading_rate"]'));
    let coats = parseFloat(coatsInput?.value?.trim() || '');
    if (!Number.isFinite(coats) || coats < 1) coats = 1;
    const rate = parseFloat(rateInput?.value?.trim() || '1') || 1;
    const totalAreaToCover = netArea * coats;
    const totalLiters = rate > 0 ? totalAreaToCover / rate : 0;
    return { totalWallArea, subtractedArea, netArea, totalAreaToCover, totalLiters, coats, spreadingRate: rate };
  }

  /** Sum of (Length * Height * Multiply by) for each row (wall or opening). */
  function sumRowAreas(/** @type {NodeListOf<Element>|Element[]} */ rows) {
    let sum = 0;
    for (const row of rows) {
      const inputs = row.querySelectorAll('input');
      const rawLength = parseFloat(/** @type {HTMLInputElement} */ (inputs[0])?.value || '');
      const rawHeight = parseFloat(/** @type {HTMLInputElement} */ (inputs[1])?.value || '');
      const rawMultiply = parseFloat(/** @type {HTMLInputElement} */ (inputs[2])?.value || '');

      const length = Number.isFinite(rawLength) && rawLength > 0 ? rawLength : 0;
      const height = Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : 0;
      let multiply = Number.isFinite(rawMultiply) ? rawMultiply : 1;
      if (multiply < 1) multiply = 1;

      sum += length * height * multiply;
    }
    return sum;
  }

  /** Hides the results block (e.g. when popup opens or closes). */
  function hideResults(/** @type {HTMLElement} */ root) {
    const resultsBlock = root.querySelector(`[${DATA.results}]`);
    if (resultsBlock) /** @type {HTMLElement} */ (resultsBlock).hidden = true;
  }

  /** Fills result elements and shows the results block. */
  function showResults(/** @type {HTMLElement} */ root, /** @type {{ totalWallArea: number; subtractedArea: number; netArea: number; totalAreaToCover: number; totalLiters: number; coats: number; spreadingRate: number }} */ data) {
    const resultsBlock = root.querySelector(`[${DATA.results}]`);
    if (!resultsBlock) return;
    const format = (/** @type {number} */ n) => Number(n).toFixed(2).replace('.', ',');
    setText(root.querySelector(`[${DATA.totalWallValue}]`), format(data.totalWallArea) + ' m²');
    setText(root.querySelector(`[${DATA.subtractedValue}]`), format(data.subtractedArea) + ' m²');
    setText(root.querySelector(`[${DATA.netValue}]`), format(data.netArea) + ' m²');
    setText(root.querySelector(`[${DATA.litersValue}]`), format(data.totalLiters));
    setText(root.querySelector(`[${DATA.totalArea}]`), format(data.totalAreaToCover) + ' m²');
    // "For X coats" label (template translated in Liquid; JS only injects the number).
    const coatsTemplate = root.getAttribute('data-paint-for-coats-template') || '';
    if (coatsTemplate) {
      const coatsText = coatsTemplate.replace('__COUNT__', String(data.coats));
      setText(root.querySelector(`[${DATA.coatsLabel}]`), coatsText);
    }
    // Template is translated in Liquid (locale); JS only inserts calculated numbers.
    const template = root.getAttribute('data-paint-based-on-template') || '';
    const basedOnText = template
      .replace('__COUNT__', String(data.coats))
      .replace('__RATE__', format(data.spreadingRate));
    setText(root.querySelector(`[${DATA.basedOn}]`), basedOnText);
    /** @type {HTMLElement} */ (resultsBlock).hidden = false;

    // Scroll the results into view inside the dialog so users see the outcome.
    if (typeof resultsBlock.scrollIntoView === 'function') {
      resultsBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function setText(/** @type {Element|null} */ el, /** @type {string} */ text) {
    if (el) el.textContent = text;
  }

  /** Appends a new wall or opening row; clones first row, updates label and input ids. */
  function appendRow(/** @type {HTMLElement} */ root, /** @type {'wall'|'opening'} */ type) {
    const isWall = type === 'wall';
    const list = root.querySelector(isWall ? `[${DATA.wallsList}]` : `[${DATA.openingsList}]`);
    const template = root.querySelector(isWall ? `[${DATA.wallItem}]` : `[${DATA.openingItem}]`);
    if (!list || !template) return;

    const prefix = root.getAttribute(isWall ? 'data-wall-label-prefix' : 'data-opening-label-prefix') || (isWall ? 'Wall ' : 'Opening ');
    const count = root.querySelectorAll(isWall ? `[${DATA.wallItem}]` : `[${DATA.openingItem}]`).length + 1;
    const clone = /** @type {HTMLElement} */ (template.cloneNode(true));

    const labelEl = clone.querySelector(`[${DATA.rowLabel}]`);
    if (labelEl) labelEl.textContent = prefix + count;

    const header = clone.querySelector(`[${DATA.rowHeader}]`);
    if (header && !clone.querySelector(`[${DATA.remove}]`)) {
      header.appendChild(createRemoveButton(root));
    }

    const rootId = root.id || 'paint-calc';
    const prefixId = isWall ? `wall${count}` : `open${count}`;
    const ids = [`${rootId}-${prefixId}-length`, `${rootId}-${prefixId}-height`, `${rootId}-${prefixId}-multiply`];
    const inputs = clone.querySelectorAll('input');
    const labels = clone.querySelectorAll('label');
    inputs.forEach((input, i) => {
      input.id = ids[i] || `${rootId}-${prefixId}-${i}`;
      if (input.type === 'number') {
        // Reset numeric values: keep "Multiply by" at 1, others empty.
        input.value = i === 2 ? '1' : '';
      }
    });
    labels.forEach((label, i) => {
      const inp = inputs[i];
      if (inp && inp.id) label.setAttribute('for', inp.id);
    });

    list.appendChild(clone);
  }

  /** Returns a remove button; label from data-remove-label on root. */
  function createRemoveButton(/** @type {HTMLElement} */ root) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute(DATA.remove, '');
    btn.className = 'paint-calculator__remove-row button button-unstyled';
    btn.textContent = root.getAttribute('data-remove-label') || 'Remove';
    return btn;
  }

  /** Delegated click: remove wall or opening row when remove button is clicked. */
  function onRemoveClick(/** @type {MouseEvent} */ e) {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest(`[${DATA.remove}]`);
    if (!btn) return;
    const row = btn.closest(`[${DATA.wallItem}]`) || btn.closest(`[${DATA.openingItem}]`);
    if (row) {
      e.preventDefault();
      e.stopPropagation();
      row.remove();
    }
  }

  /**
   * Enforces min/max constraints on a number input element, preventing
   * negative or zero values where a positive minimum is defined.
   */
  function enforceNumberConstraints(/** @type {HTMLInputElement} */ input) {
    const raw = input.value;
    if (raw === '' || raw === '-' || raw === '+') return;

    let value = parseFloat(raw);
    if (!Number.isFinite(value)) {
      input.value = '';
      return;
    }

    const minAttr = input.getAttribute('min');
    if (minAttr !== null && minAttr !== '') {
      const min = Number(minAttr);
      if (Number.isFinite(min) && value < min) {
        value = min;
      }
    }

    const maxAttr = input.getAttribute('max');
    if (maxAttr !== null && maxAttr !== '') {
      const max = Number(maxAttr);
      if (Number.isFinite(max) && value > max) {
        value = max;
      }
    }

    input.value = String(value);
  }

  if (!customElements.get('paint-calculator')) {
    customElements.define('paint-calculator', PaintCalculator);
  }
})();
